import { IconContentMathPlus } from '@doltech/icons/IconContentMathPlus';
import { noop } from 'lodash';
import * as React from 'react';
import styled from 'styled-components';

import { deviceBreakpoints } from '../../../../hooks/useDeviceQuery.hook';
import { getNodes } from '../../../../slatePlugins/common/queries/getNodes';
import { useBlankEditorDiagramLabel } from '../../../BlogEditor/OnlineTestEditorPlugin.presets';
import { Button } from '../../../ButtonV2/ButtonV2';
import { figmaColors } from '../../../colors';
import { colorsV2 } from '../../../colors-v2';
import { DolEditor } from '../../DolEditor';
import { addBlank } from '../../elements/editable-blank-voids/transforms/addBlank';
import { EDITABLE_BLANK_VOID } from '../../elements/editable-blank-voids/types';
import { QuestionSideToolbar } from '../../toolbars/sidebar/QuestionSideToolbar';
import { EditorProps } from '../../types';

const Main = styled.div`
  .dol-editor {
    .editor-content-wrapper {
      margin-top: 8px;
      display: flex;
      flex-direction: row;
      align-items: center;
    }
    .editor-editable-plugins {
      flex: 1;
      word-break: break-word;
      box-sizing: border-box;
      border-radius: 8px;
      background: ${colorsV2.white100};
      border: none;
      padding: 0;
    }
  }

  @media ${deviceBreakpoints.fromTablet} {
    .dol-editor {
      .editor-editable-plugins {
        background: ${figmaColors.bg2};
        border: 1.5px dashed ${figmaColors.neutral1};
        padding: 8px 12px;
      }
    }
  }

  @media ${deviceBreakpoints.mobile} {
    .dol-editor {
      .editor-editable-plugins {
        padding: 8px 0;
      }
    }
  }
`;

interface AddBlankButtonProps {
  readOnly?: boolean;
  editor?: any;
}

export const AddBlankButton = ({ readOnly, editor }: AddBlankButtonProps) => {
  return (
    <Button.Secondary
      className="btn-add-blank"
      variant="outline"
      style={{ opacity: readOnly ? 0 : 1 }}
      iconLeft={<IconContentMathPlus />}
      onMouseDown={(event) => {
        event.preventDefault();
        addBlank(editor);
      }}
    >
      Add blank
    </Button.Secondary>
  );
};

interface InputBlankProps extends Omit<EditorProps, 'initialValue'> {
  questionNo?: number;
  onEditorChange?: (blanks: any[]) => void;
  initBlanks?: any[];
  userAnswers?: any[];
  selectBlankIndex?: number;
  onUserAnswerChange?: ({ blankId, text }: { blankId: string; text: string }) => void;
  onClickBlank?: (questionNo, isScroll) => void;
}

export const getAllBlankNodeFromEditor = ({ editor }) => {
  const nodes = getNodes(editor, {
    match: { type: EDITABLE_BLANK_VOID } as any,
    at: [],
  });
  const blanksToUpdate = [];
  let blankIndex = 0;
  for (const [node, range] of nodes) {
    blanksToUpdate.push({
      ...node.children[0],
      blankIndex,
      range,
    });
    blankIndex += 1;
  }
  return blanksToUpdate;
};

export const InputBlank = React.memo((props: InputBlankProps) => {
  const {
    questionNo = 1,
    onEditorChange = noop,
    initBlanks = [],
    readOnly,
    userAnswers,
    mode,
    selectBlankIndex,
    onUserAnswerChange,
    onClickBlank,
    ...rest
  } = props;
  const [blanks, setBlanks] = React.useState(initBlanks);
  const { plugins, editorChains } = useBlankEditorDiagramLabel({
    blanks,
    questionNo,
    readOnly,
    mode,
    userAnswers,
    selectBlankIndex,
    onUserAnswerChange,
    onClickBlank,
  });
  const ref = React.useRef<any>();

  return (
    <Main ref={ref} className="input-blank">
      <DolEditor
        readOnly={readOnly}
        placeholder=""
        plugins={plugins}
        editorChains={editorChains}
        withToolbar={({ editor }) => {
          return (
            <>
              <AddBlankButton editor={editor} readOnly={readOnly} />;
              <QuestionSideToolbar containerRef={ref} />
            </>
          );
        }}
        renderElementDeps={[blanks, questionNo, readOnly, mode]}
        onEditorChange={(node, editor) => {
          if (readOnly) return;
          const blanksToUpdate = getAllBlankNodeFromEditor({ editor });
          setBlanks(blanksToUpdate);
          onEditorChange({
            editorValue: node,
            blanks: blanksToUpdate,
          });
        }}
        {...rest}
      />
    </Main>
  );
});
